import {
  find,
  filter,
  reduce,
  includes,
  orderBy,
  map,
  forEach
} from 'lodash'
import { getData, postData } from '@/helpers/util/webApiUtil'
import { TagList } from '@/data/@types/MyCollectionTagList'
import { MyCollectionItem } from '@/data/@types/MyCollectionItem'
import { Contents } from '@/data/@types/MyCollectionContents'
import { MyCollectionRemoveItem } from '@/data/@types/MyCollectionRemoveItem'
import StringKeyObject from '@/data/@types/StringKeyObject'

const BASE_URL = process.env.VUE_APP_API_BASE_URL

const SORT_SCORE = 'SCORE' // 適合度順
const SORT_TITLE_ASC = 'TITLE_ASC' // タイトル：昇順
const SORT_TITLE_DESC = 'TITLE_DESC' // タイトル：降順
const SORT_CREATOR_ASC = 'PUBLISHER_ASC' // 著者：昇順
const SORT_CREATOR_DESC = 'PUBLISHER_DESC' // 著者：降順
const SORT_ISSUED_ASC = 'COLLECTION_ASC' // 出版日：新しい順
const SORT_ISSUED_DESC = 'COLLECTION_DESC' // 出版日：古い順
const SORT_CALL_NUMBER_ASC = 'BILL_NUMBER_ASC' // 請求記号：昇順
const SORT_CALL_NUMBER_DESC = 'BILL_NUMBER_DESC' // 請求記号：降順

/**
 * ステート
 */
type State = {
  order: number,
  // コレクションマスタ
  itemList: MyCollectionItem[],
  tagList: TagList,
  isProcessing: false,
  keyword: string,
  selectedTagId: number,
  sort: string,
  removeList: MyCollectionRemoveItem[],
  notificationToasts: { pid: number, bid: string, cid: string, type: string, text: string }[]
  thumbnailTokenList: any,
  notificationNotLoggedIn: boolean,
}

const state: State = {
  order: 0,
  // コレクションマスタ
  itemList: [],
  tagList: {},
  isProcessing: false,
  keyword: '',
  selectedTagId: 0,
  sort: 'SCORE',
  removeList: [],
  notificationToasts: [],
  thumbnailTokenList: [],
  notificationNotLoggedIn: false
}

/**
 * ゲッター
 */

const getters = {
  myCollectionList (state: any) {
    return modifyItemList(state.itemList, state.sort, state.keyword, state.selectedTagId, state.thumbnailTokenList)
  },
  myCollectionThumbnail (state:any, getters: any) {
    const itemList: MyCollectionItem[] = getters.myCollectionList
    const thumbnailList: any = []

    itemList.forEach(item => {
      thumbnailList.push({
        pid: item.pid,
        tagIds: item.tagIds,
        contents: [],
        updateAt: item.updateAt,
        title: item.title,
        contentTitle: false,
        contentLink: false,
        author: item.author,
        publisher: item.publisher,
        permission: item.permission,
        collection: item.collection,
        billNumber: item.billNumber,
        contentsNumber: false,
        thumbnail: item.thumbnail,
        isItem: true,
      })

      if (item.contents) {
        item.contents.forEach(content => {
          const contentsName: any = content.name || ''
          const contentsNameJa: string = contentsName.ja || ''
          const contentsThumbnail: any = content.publicPath || item.thumbnail

          thumbnailList.push({
            pid: item.pid, // リンクパスとして利用する
            tagIds: item.tagIds,
            contents: [],
            updateAt: item.updateAt,
            title: item.title,
            contentTitle: contentsNameJa,
            contentLink: content.bundleNumber && content.contentsNumber ? `${item.pid}/${content.bundleNumber}/${content.contentsNumber}` : item.pid, // リンクパスとして利用する
            author: item.author,
            publisher: item.publisher,
            permission: item.permission,
            collection: item.collection,
            billNumber: item.billNumber,
            contentsNumber: content.contentsNumber,
            thumbnail: contentsThumbnail,
            isItem: false,
          })
        })
      }
    })

    return thumbnailList
  },
  myCollectionTagList (State: any) {
    const tagList = state.tagList
    const itemList = state.itemList

    return map(tagList, (tagItem, tagId) => {
      const hit = reduce(itemList, (res, item) => {
        if (
          item.tagIds.filter((id) => {
            return tagId === String(id)
          }).length
        ) res++

        return res
      }, 0)

      return {
        id: Number(tagId),
        label: tagItem,
        hit
      }
    })
  },
  myCollectionItemCount (state: any, getters: any) {
    const itemList: MyCollectionItem[] = getters.myCollectionList
    return itemList.length
  },
  myCollectionKeyword (state: any) {
    return state.keyword
  },
  myCollectionSort (state: any) {
    return state.sort
  },
  notificationToasts (state: any) {
    // return state.notificationToasts
    if (!state.removeList.length) return []
    return state.removeList.map((item: MyCollectionRemoveItem) => {
      return {
        pid: item.pid,
        bid: item.bid,
        cid: item.cid,
        type: 'notice',
        text: 'bookmarkRemove'
      }
    })
  },
  myCollectionSelectedTagName (state: any) {
    const tagName = state.tagList[state.selectedTagId]
    return tagName
  },
  notificationNotLoggedIn (state: any) {
    return state.notificationNotLoggedIn
  },
  myCollectionIsProcessing (state: State): boolean {
    return state.isProcessing
  },
}

/**
 * ミューテーション
 */

const mutations = {
  MY_COLLECTION_SET (state: any, items: MyCollectionItem[]) {
    if (items) state.itemList = items || []
  },
  MY_COLLECTION_SET_TAG_LIST (state: any, tags: TagList) {
    if (tags) state.tagList = tags || {}
  },
  MY_COLLECTION_SELECTED_TAG (state: any, tagId: number) {
    state.selectedTagId = tagId || 0
  },
  MY_COLLECTION_PROCESSING (state: any) {
    state.isProcessing = true
  },
  MY_COLLECTION_PROCESSED (state: any) {
    state.isProcessing = false
  },
  MY_COLLECTION_SELECT_DISPLAY_MODE (state: any, mode: string) {
    state.sort = mode
  },
  MY_COLLECTION_REMOVE_ADD_ITEM (state: any, rItem: any) {
    state.removeList.push(rItem)
    state.itemList = displayFormat(state.itemList, state.removeList)
  },
  MY_COLLECTION_DELETE_ITEM (state: State, targetItem: MyCollectionRemoveItem) {
    const machDeleteItem = (item: MyCollectionRemoveItem) => {
      return (
        targetItem.pid !== item.pid &&
        targetItem.bid !== item.bid &&
        targetItem.cid !== item.cid
      )
    }

    state.removeList = state.removeList.filter(machDeleteItem)
  },
  MY_COLLECTION_RESET_ITEM (state: State, targetItem: MyCollectionRemoveItem) {
    const machDeleteItem = (item: MyCollectionRemoveItem) => {
      return (
        targetItem.pid !== item.pid &&
        targetItem.bid !== item.bid &&
        targetItem.cid !== item.cid
      )
    }
    state.removeList = state.removeList.filter(machDeleteItem)
  },
  // keyword
  MY_COLLECTION_KEY_WORD (state: State, word: any) {
    if (!word) state.keyword = ''
    state.keyword = word
  },
  SET_ITEM_CONTENTS_THUMBNAIL_TOKEN (state: State, updateItem: any) {
    state.itemList = map(state.itemList, (item) => {
      const contents = item.contents || false
      if (!contents) return item
      if (item.pid !== updateItem.pid) return item
      return updateItem
    })
  },
  // 非同期でコンテンツサムネイルのアクセストークンを取得する
  SET_CONTENTS_THUMBNAIL_TOKEN (state:State, thumbnailTokenList: any) {
    // getContentsThumbnailToken
    state.thumbnailTokenList = thumbnailTokenList
  },
  // ログアウトしたらマイコレクションをクリアする
  MY_COLLECTION_CLEAR (state: State) {
    state.itemList = []
    state.tagList = {}
  },
  NotLoggedIn (state: State, is: boolean) {
    state.notificationNotLoggedIn = is
  }
}

/**
 * アクション
 */

const actions = {
  // 画面表示の初期処理
  async myCollectionInit (context: any) {
    await context.commit('MY_COLLECTION_PROCESSING')
    await context.dispatch('myCollectionCollectionList')
    await context.commit('MY_COLLECTION_PROCESSED')
  },
  // マイコレクションアイテムの再読み込み。ローダーは表示しない
  async myCollectionCollectionList (context: any) {
    const url = `${BASE_URL}/mycollection/collection/list`
    try {
      const response = await getData(url)
      if (response.items) {
        context.commit('MY_COLLECTION_SET', response.items)
      } else {
        context.commit('MY_COLLECTION_SET', [])
      }
      if (response.tags) {
        context.commit('MY_COLLECTION_SET_TAG_LIST', response.tags)
      } else {
        context.commit('MY_COLLECTION_SET_TAG_LIST', [])
      }
      context.dispatch('getContentsThumbnailToken', response.items)
    } catch (error: any) {
      // TODO エラーハンドリング
      console.error(error.message)
    }
  },
  myCollectionSelectDisplayMode (context: any, mode: string) {
    context.commit('MY_COLLECTION_SELECT_DISPLAY_MODE', mode)
  },
  async myCollectionAddRemoveList (context: any, query: MyCollectionRemoveItem) {
    context.commit('MY_COLLECTION_REMOVE_ADD_ITEM', query)
    const url = `${BASE_URL}/mycollection/collection/delete`
    try {
      await postData(url, {
        pid: query.pid,
        bid: query.bid || null, // string=コンテンツ アイテム=false> null
        cid: query.cid || null // string=コンテンツ アイテム=false> null
      })
      context.dispatch('myCollectionCollectionList')
    } catch (error: any) {
      // TODO エラーハンドリング
      console.error(error.message)
    }
  },
  myCollectionDeleteItem (context: any, query: MyCollectionRemoveItem) {
    context.commit('MY_COLLECTION_DELETE_ITEM', query)
  },
  async mycollectionCollectionEntry (context: any, query: any) {
    const url = `${BASE_URL}/mycollection/collection/entry`
    try {
      const res = await postData(url, query)
      context.dispatch('myCollectionCollectionList')
    } catch (error: any) {
      // TODO エラーハンドリング
      console.error(error.message)
    }
  },
  async myCollectionItemLink (context: any, query: MyCollectionRemoveItem) {
    const url = `${BASE_URL}/mycollection/item/link`
    try {
      const res = await postData(url, query)
      context.dispatch('myCollectionCollectionList')
    } catch (error: any) {
      // TODO エラーハンドリング
      console.error(error.message)
    }
  },
  async myCollectionItemUnlink (context: any, query: any) {
    const url = `${BASE_URL}/mycollection/item/unlink`

    try {
      const res = await postData(url, query)
      context.dispatch('myCollectionCollectionList')
    } catch (error: any) {
      // TODO エラーハンドリング
      console.error(error.message)
    }
  },
  async getContentsThumbnailToken (context: any, itemList: MyCollectionItem[]) {
    if (!itemList) {
      context.commit('SET_CONTENTS_THUMBNAIL_TOKEN', [])
      return
    }
    const getPid = (pid:any) => {
      return pid ? `info:ndljp/pid/${pid}` : ''
    }
    const result = await Promise.all(itemList.map(async (item) => {
      const contents = item.contents || false
      if (!contents) return false

      const pid: string = getPid(item.pid)

      return await getData(`${BASE_URL}/restriction/issue/token/${pid}`)
        .then(async (response: any) => {
          const tokens = await response.tokens
          const timestamp = await response.timestamp

          return {
            pid: item.pid,
            tokens,
            timestamp,
          }
        })
    }))
    context.commit('SET_CONTENTS_THUMBNAIL_TOKEN', filter(result, v => v !== false))
  },
}

const displayFormat = (list:MyCollectionItem[], rList:MyCollectionRemoveItem[]) => {
  if (!rList.length) return list

  // 削除リストからアイテム指定のものだけをピックアップして処理
  const searchItem = (itm: MyCollectionItem) => {
    const isTargetItem = (rItm:MyCollectionRemoveItem) => {
      return itm.pid === rItm.pid && !rItm.bid && !rItm.cid
    }
    const targetItem = rList.filter(isTargetItem)

    return targetItem.length === 0
  }

  // 削除リストからコンテンツ指定のものだけをピックアップして処理
  const searchContent = (itm: MyCollectionItem) => {
    if (!itm.contents) return itm.contents || []
    const contentForEach = (content: Contents) => {
      const isTargetContent = (rItm: MyCollectionRemoveItem) => {
        return itm.pid === rItm.pid && content.bid === rItm.bid && content.cid === rItm.cid
      }
      return rList.filter(isTargetContent).length === 0
    }

    return itm.contents.filter(contentForEach)
  }

  const contentsOutItemList = list.filter(searchItem)
  const contentsOnItemList = contentsOutItemList.map((item) => {
    return {
      author: item.author,
      billNumber: item.billNumber,
      collection: item.collection,
      contents: searchContent(item),
      permission: item.permission,
      pid: item.pid,
      publishDate: item.publishDate,
      publisher: item.publisher,
      tagIds: item.tagIds,
      title: item.title,
      thumbnail: item.thumbnail,
      updateAt: item.updateAt,
    }
  })

  return contentsOnItemList
}

// 文字列検索
const includeList = (list: MyCollectionItem[], keyword: string) => {
  return reduce(list, (res: MyCollectionItem[], item: MyCollectionItem) => {
    if (includes(item.title, keyword)) {
      res.push(item)
    }

    return res
  }, [])
}

// 文字列検索をするのか
const isSearchWord = (keyword: string) => {
  if (keyword === '') return false
  return keyword
}

// tagList選択
const tagSelectedList = (list: MyCollectionItem[], tagId: number) => {
  return reduce(list, (res: MyCollectionItem[], item: MyCollectionItem) => {
    if (includes(item.tagIds, tagId)) {
      res.push(item)
    }

    return res
  }, [])
}

const mergeContentsThumbnailToken = (list: MyCollectionItem[], tokenList: any) => {
  if (tokenList.length === 0) return list
  const res = map(list, (item) => {
    const hasContents = item.contents || false
    if (!hasContents) return item
    const targetToken = filter(tokenList, (v) => v.pid === item.pid)[0]
    const contents = map(item.contents, content => {
      const encode = reduce(targetToken.tokens, (r: any, v, k) => {
        if (k === content.cid) {
          r = encodeURIComponent(
            JSON.stringify({
              token: targetToken.tokens[k],
              timestamp: targetToken.timestamp
            })
          )
        }

        return r
      }, false)

      const path = `contents/${item.pid}/${content.bid}/${content.cid}/thumbnail.jpg`
      const token = encode ? `?token=${encode}` : ''
      const publicPath = `${path}${token}`
      return { ...content, ...{ publicPath } }
    })

    return { ...item, ...{ contents } }
  })

  return res
}

const modifyItemList = (li:MyCollectionItem[], sort: string, keyword: string, tagId: number, tokens: any) => {
  if (li.length === 0) return []
  const keyWordList:MyCollectionItem[] = (isSearchWord(keyword)) ? includeList(li, keyword.replace(/\u{3000}/g, ' ').replace(/\(/g, '\\(').replace(/\)/g, '\\)').trim()) : li
  const list:MyCollectionItem[] = mergeContentsThumbnailToken(tagSelectedList(keyWordList, tagId), tokens)

  const sorting: StringKeyObject = {
    [SORT_SCORE]: { key: 'updateAt', by: 'desc' },
    [SORT_TITLE_ASC]: { key: 'title', by: 'asc' },
    [SORT_TITLE_DESC]: { key: 'title', by: 'desc' },
    [SORT_CREATOR_ASC]: { key: 'author', by: 'asc' },
    [SORT_CREATOR_DESC]: { key: 'author', by: 'desc' },
    [SORT_ISSUED_ASC]: { key: 'publishDate', by: 'asc' },
    [SORT_ISSUED_DESC]: { key: 'publishDate', by: 'desc' },
    [SORT_CALL_NUMBER_ASC]: { key: 'billNumber', by: 'asc' },
    [SORT_CALL_NUMBER_DESC]: { key: 'billNumber', by: 'desc' },
    default: { key: false, order: false }
  }
  const order = sorting[sort]

  // コンテンツをcontentsNumber順にソート（1→2→3→4→5）
  const contentsSort = (li:MyCollectionItem[]) => {
    return map(li, (item) => {
      if (!item.contents || !item.contents.length) {
        return item
      }

      const itemSorted = orderBy(item.contents, (content) => {
        return Number(content.contentsNumber)
      })

      return { ...item, ...{ contents: itemSorted } }
    })
  }

  return orderBy(contentsSort(list), (item) => {
    const key: string = order.key || ''
    const itm: StringKeyObject = item
    return itm[key]
  }, order.by)
}

export default {
  state,
  getters,
  mutations,
  actions
}
